import { coerce }  from '@meltwater/coerce';
import { DocumentGridOptions } from '../options';
import { GydaDocument } from '../../gyda-document/index.js';
import {
    CUSTOM_KEY_METRIC,
    DOCUMENT_GRID_ITEM_CLICK_EVENT_NAME,
    isValidKeyMetric
} from '../../key-metric';
import { SeriesData } from '@meltwater/mw-visualizations-core';
import { PrDocumentCard } from '../../pr-document-card';
import { version } from '../../version.js';

const DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY = '';

/**
 * Event fired when the grid has been loaded with documents.
 *
 * @event DocumentGrid#event:DOCUMENT_GRID_LOADED
 * @prop {number} documentCount - The number of documents in the grid
 * @prop {number} estimatedGridHeight - The estimated height of the grid in pixels
 */

/**
 * Name to listen for {@link DocumentGrid#event:DOCUMENT_GRID_LOADED} events
 */
export const DOCUMENT_GRID_LOADED_EVENT_NAME = 'pr-document-grid-loaded';

const ROW_HEIGHT = 278;
const ROW_GAP = 16;

function estimateGridHeight({ documentCount, gridWidth }) {
    /* eslint-disable no-magic-numbers */

    let estimatedRowCount = Math.ceil(documentCount / 5);
    if (gridWidth < 322) {
        estimatedRowCount = documentCount;
    } else if (gridWidth < 495) {
        estimatedRowCount = Math.ceil(documentCount / 2);
    } else if (gridWidth < 651) {
        estimatedRowCount = Math.ceil(documentCount / 3);
    } else if (gridWidth < 825) {
        estimatedRowCount = Math.ceil(documentCount / 4);
    }

    const estimatedHeight = estimatedRowCount * (ROW_HEIGHT + ROW_GAP);

    return estimatedHeight;
    /* eslint-enable no-magic-numbers */
}

const [ MAJOR_VERSION ] = version.split('.');

/**
 * This visualizes an array of Gyda documents as a multi-row grid suitable for
 * inclusion in applications and reports.
 *
 * Series data provided to this visualization can be used to display a "key
 * metric" on the card which may not natively be a part of the document.
 *
 * @example
 * const grid = document.createElement(DocumentGrid.tagName);
 * grid.setData(seriesData, { companyId, documents });
 * grid.setOptions({ keyMetric: 'ave', language: 'en', yAxisTitle: 'USD' });
 * container.appendChild(grid);
 */
export class DocumentGrid extends HTMLElement {
    #clickListener;
    #companyId;
    #documents = [];
    #documentGridContainer;
    #seriesData = null;
    #vizOptions = new DocumentGridOptions({});

    /**
     * The tag name for the automatically-defined custom element.
     *
     * The value is specific to a major version so as to provide forward
     * compatibility by avoiding collisions with other copies of the library
     * that may be defined as well.
     *
     * @returns {string} The major version-specific tag name for the defined custom element
     */
    static get tagName() {
        return `pr-document-grid-v${MAJOR_VERSION}`;
    }

    #buildDocumentList() {
        const fluxContentCards = this.#documents
            .map((gydaDocument, index) => {
                if (!gydaDocument) {
                    return undefined;
                }

                const contentCard = document.createElement(PrDocumentCard.tagName);

                const key = this.#seriesData?.data[index]?.key === gydaDocument.documentId
                    ? DOCUMENT_KEY_FOR_UNAVAILABLE_TITLE_AND_BODY
                    : this.#seriesData?.data[index]?.key;

                contentCard.setData({
                    companyId: this.#companyId,
                    document: gydaDocument,
                    key,
                    language: this.#vizOptions.language,
                    keyMetric: this.#vizOptions.keyMetric,
                    keyMetricLabel: this.#vizOptions.yAxisTitle,
                    value: this.#seriesData?.data[index].value
                });

                return contentCard;
            })
            .filter(element => Boolean(element));

        this.#documentGridContainer.replaceChildren(...fluxContentCards);

        const documentCount = fluxContentCards.length;

        const width = this.#documentGridContainer.clientWidth;

        const estimatedHeight = estimateGridHeight({ documentCount, gridWidth: width });

        const documentGridLoadedEvent = new CustomEvent(DOCUMENT_GRID_LOADED_EVENT_NAME, {
            detail: {
                documentCount: fluxContentCards.length,
                estimatedGridHeight: estimatedHeight
            },
            bubbles: true,
            cancelable: true,
            composed: true
        });

        this.dispatchEvent(documentGridLoadedEvent);
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        this.shadowRoot.innerHTML = /*html*/`
            <style>
                #host {
                    display: flex;
                }
                #grid-container {
                    display: flex;
                    gap: 12px 16px;
                    flex-wrap: wrap;
                    justify-content: center;
                    overflow: auto;
                    padding: 0 4px 4px;
                }

                ${PrDocumentCard.tagName} {
                    border: 1px solid var(--flux-border-color);
                    border-radius: 2.864px;
                    box-shadow: var(--flux-shadow-1);
                    flex: 1 1 auto;
                    height: 278px;
                    max-width: 430px;
                }

                #grid-container:has(> *:nth-child(4)) > ${PrDocumentCard.tagName} {
                    flex: 1 1 50%;
                    max-width: 450px
                }

                #grid-container:has(> *:nth-child(5)) > ${PrDocumentCard.tagName} {
                    flex: 1 1 30%;
                }

                #grid-container:has(> *:nth-child(6)) > ${PrDocumentCard.tagName} {
                    flex: 1 1 19%;
                }

                #grid-container:has(> *:nth-child(7)) > ${PrDocumentCard.tagName} {
                    flex: 1 1 16%;
                }
            </style>
            <div id="grid-container"></div>
        `;
        this.#documentGridContainer = this.shadowRoot.querySelector('#grid-container');
    }

    /**
     * Render the grid according to the data and options when attaching to
     * the DOM.
     *
     * @returns {undefined}
     */
    connectedCallback() {
        this.render();
    }

    /**
     * Set the data to display in the document grid
     *
     * @example
     * const grid = document.createElement(DocumentGrid.tagName);
     * grid.setData(seriesData, { companyId, documents });
     *
     * @param {SeriesData} [seriesData=null] - The series data for any key metric values to disply. The order **must** align with the documents. If provided, the key value will be shown as the document title.
     * @param {object} metadata - The metadata
     * @param {string} metadata.companyId - The companyId to use for access permission with the premium document viewer
     * @param {GydaDocument[]} metadata.documents - The documents to render in the grid
     *
     * @returns {undefined}
     */
    setData(seriesData = null, { companyId, documents }) {
        this.#companyId = companyId;
        this.#documents = documents.map(document => (document !== null ? coerce(document, GydaDocument, 'document must be a GydaDocument') : null));

        this.#seriesData = seriesData !== null
            ? coerce(seriesData, SeriesData, 'seriesData must be coerce-able to SeriesData')
            : null;
    }

    /**
     * Apply optional customizations to the display of the document grid.
     *
     * @example
     * const grid = document.createElement(DocumentGrid.tagName);
     * grid.setOptions({ keyMetric: 'ave', language: 'en', yAxisTitle: 'USD' });
     *
     * @param {DocumentGridOptions} options - The options to apply to the document grid
     *
     * @returns {undefined}
     */
    setOptions(options) {
        const vizOptions = coerce(options, DocumentGridOptions, 'options must be valid DocumentGridOptions');

        if (!isValidKeyMetric(vizOptions.keyMetric)) {
            throw new RangeError(`A valid key metric value or constant must be provided. Provided value: ${vizOptions.keyMetric?.toString()}`);
        }

        if (vizOptions.keyMetric === CUSTOM_KEY_METRIC && (this.#seriesData === null || vizOptions.yAxisTitle === undefined)) {
            throw new RangeError(`A custom key metric value requires seriesData for the metric values and a yAxisTitle for the metric label. Provided seriesData length: ${this.#seriesData?.data.length}; Provided yAxisTitle: ${vizOptions.yAxisTitle}`);
        }

        this.#vizOptions = vizOptions;

        if (this.#clickListener) {
            this.#documentGridContainer.removeEventListener(DOCUMENT_GRID_ITEM_CLICK_EVENT_NAME, this.#clickListener);
            this.#clickListener = null;
        }
    }

    /**
     * Render the grid according to its data and options.
     *
     * This is only necessary to handle changes *after* the element has been
     * attached to the DOM.
     *
     * @returns {undefined}
     */
    render() {
        this.#buildDocumentList();
    }
}

if (!customElements.get(DocumentGrid.tagName)) {
    customElements.define(DocumentGrid.tagName, DocumentGrid);
}
